import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Mail, Camera, Check, ArrowRight, X } from "lucide-react";
import api from "@/lib/api";

const STEPS = [
    {
        key: "bank_connected",
        title: "Reliez votre banque",
        body: "CSV de votre relevé bancaire ou Revolut Business pour récupérer toutes vos transactions par carte.",
        cta: "Aller à Connexions",
        href: "/app/connections",
        icon: CreditCard,
    },
    {
        key: "email_connected",
        title: "Reliez votre boîte mail",
        body: "Connectez Gmail pour que les e-reçus soient attachés automatiquement à vos paiements.",
        cta: "Connecter Gmail",
        href: "/app/connections",
        icon: Mail,
    },
    {
        key: "has_receipts",
        title: "Scannez votre premier ticket",
        body: "Photographiez un ticket papier — l'IA en extrait tout (articles, TVA, SIRET) en 5 secondes.",
        cta: "Scanner un ticket",
        href: "/app",
        icon: Camera,
    },
];

export default function OnboardingWizard() {
    const [status, setStatus] = useState(null);
    const [dismissed, setDismissed] = useState(() => localStorage.getItem("tk_onboarding_dismissed") === "1");

    useEffect(() => {
        if (dismissed) return;
        api.get("/onboarding/status").then((r) => setStatus(r.data)).catch(() => {});
        // `api` is a module-level singleton; `setStatus` is a stable setter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dismissed]);

    if (dismissed || !status || status.completed) return null;

    const dismiss = () => {
        localStorage.setItem("tk_onboarding_dismissed", "1");
        setDismissed(true);
    };

    return (
        <div className="tk-card p-6 sm:p-8 relative" data-testid="onboarding-wizard">
            <button
                onClick={dismiss}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F5F5F7] hover:bg-[#E5E5EA] flex items-center justify-center"
                data-testid="onboarding-dismiss"
                aria-label="Fermer"
            >
                <X className="w-4 h-4 text-[#86868B]" />
            </button>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#007AFF]">Configuration · {status.steps_done}/{status.steps_total}</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[#1D1D1F]">
                3 étapes pour automatiser 90 % de vos justificatifs.
            </h2>
            <div className="mt-3 h-1.5 bg-[#F5F5F7] rounded-full overflow-hidden max-w-md">
                <div className="h-full bg-[#007AFF] rounded-full transition-all" style={{ width: `${(status.steps_done / status.steps_total) * 100}%` }} />
            </div>

            <div className="mt-8 space-y-3">
                {STEPS.map((s, i) => {
                    const done = status[s.key];
                    return (
                        <div
                            key={s.key}
                            className={`flex items-center gap-4 p-4 rounded-2xl transition ${done ? "bg-[#34C759]/10" : "bg-[#F5F5F7]"}`}
                            data-testid={`onboarding-step-${s.key}`}
                        >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${done ? "bg-[#34C759] text-white" : "bg-white text-[#1D1D1F]"}`}>
                                {done ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className={`font-medium ${done ? "text-[#1D1D1F] line-through" : "text-[#1D1D1F]"}`}>
                                    {s.title}
                                </p>
                                <p className="text-xs text-[#86868B] mt-0.5">{s.body}</p>
                            </div>
                            {!done && (
                                <Link to={s.href} className="tk-btn tk-btn-primary text-xs px-4 py-2" data-testid={`onboarding-cta-${s.key}`}>
                                    {s.cta} <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
